import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";

import config from "./config";

import "whatwg-fetch";
import "promise-polyfill/src/polyfill";
import CustomerSearch from "../components/CustomerSearch";

let base = config.api;

const postHeaders = (body: string | null): RequestInit => ({
  credentials: "include",
  method: "post",
  headers: { "Content-Type": "application/json" },
  body,
});
const postHeadersNoBody = (): RequestInit => ({
  credentials: "include",
  method: "post",
  headers: { "Content-Type": "application/json" },
});
const putHeadersNoBody = (): RequestInit => ({
  credentials: "include",
  method: "put",
  headers: { "Content-Type": "application/json" },
});
const getHeaders = {
  credentials: "include",
  method: "get",
  headers: { "Content-Type": "application/json" },
};

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface PolicyPaymentItem {
  id: string;
  include: boolean;
  status: string;
  invoice: number | null;
  reference: string;
  transactionType: string;
  effectiveDate: Date | null;
  amount: number;
  takenToDate: number;
  totalPremium: number;
  remainingToTake: number;
  suffix: number | null;
  adhocSuffix: number | null;
  adhoc?: boolean;
}

export enum CallState {
  INITIAL,
  PAUSING,
  NOT_PAUSED,
  PAUSED,
  NOT_RESUMED,
}
export enum PaymentState {
  INITIAL,
  PAYING,
  FAILED,
  SUCCESS,
}

export interface CustomerState {
  name: string;
  email: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  postCode: string;
  firstName: string;
  lastName: string;
  haveCustomer: boolean;
  error: boolean;
  headerFooterError: boolean;
  policies: PolicyPaymentItem[];
  paymentState: PaymentState;
  paymentSuccess?: boolean;
  paymentFailure?: boolean;
  paymentTimestamp?: Date;
  branch: number;
  clientRef: string;
  policyRef: string;
  transactionCode: string;
  callPaused: CallState;
  message: string | null;
  callMessage: string | null;
  redirect: string | null;
  validationErrors: Array<ErrorItem>;
  updated?: Date;
  error404?: boolean;
  header: string;
  footer: string;
  style: string;
  script: string;
  sendReceiptSuccess: boolean;
  sendReceiptError: boolean;
  url: string | null;
  token: string | null;
}

interface GetHeaderFooterResponse {
  header: string;
  footer: string;
  style: string;
  script: string;
}

interface FindResponse {
  name: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  postCode: string;
  firstName: string;
  lastName: string;
  branch: number;
  clientRef: string;
  policies: PolicyPaymentItem[];
  haveCustomer?: boolean;
}

interface PaymentResponse {
  success: boolean;
  transactionCode: string;
  message?: string;
  title?: string;
  errors?: [];
  redirect: string;
  url: string | null;
  token: string | null;
}

interface PaymentCompletedPayload {
  transactionCode: string | null;
  message: string | null;
  redirect: string | null;
  url: string | null;
  token: string | null;
}

export interface ErrorItem {
  key: string;
  reasons: Array<string>;
}
interface ErrorPayload {
  errorText: string;
  errorItems: Array<ErrorItem>;
  message: string | null;
}

interface CallOperationResponse {
  success: boolean;
  reason?: string;
}

const defaultState: CustomerState = {
  message: null,
  redirect: null,
  url: null,
  token: null,
  callMessage: null,
  validationErrors: [],
  firstName: "",
  lastName: "",
  paymentState: PaymentState.INITIAL,
  callPaused: CallState.INITIAL,
  transactionCode: null,
  clientRef: "",
  policyRef: "",
  branch: 0,
  policies: [],
  haveCustomer: false,
  error: false,
  headerFooterError: false,
  email: "",
  name: "",
  address1: "",
  address2: "",
  address3: "",
  address4: "",
  postCode: "",
  header: "",
  footer: "",
  style: "",
  script: "",
  sendReceiptSuccess: false,
  sendReceiptError: false,
};
// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface GetHeaderFooterAction {
  type: "GET_HEADER_FOOTER";
}
export interface GettingHeaderFooterAction {
  type: "GETTING_HEADER_FOOTER";
}
export interface GetHeaderFooterCompletedAction {
  type: "GET_HEADER_FOOTER_COMPLETED";
  payload: GetHeaderFooterResponse;
}
export interface GetHeaderFooterFailedAction {
  type: "GET_HEADER_FOOTER_FAILED";
  payload: { headerFooterError: boolean };
}
export interface FindAction {
  type: "FIND";
}
export interface FindingAction {
  type: "FINDING";
}
export interface FindCompletedAction {
  type: "FIND_COMPLETED";
  customer: FindResponse;
}
export interface FindFailedAction {
  type: "FIND_FAILED";
  payload: { error: boolean };
}
export interface NotFoundAction {
  type: "NOT_FOUND";
}
export interface StoreAction {
  type: "STORE";
  payload: PolicyPaymentItem[];
}
export interface ClearAction {
  type: "CLEAR";
}
export interface Paying {
  type: "PAYING";
}
export interface PayCompleted {
  type: "PAY_COMPLETED";
  payload: PaymentCompletedPayload;
}
export interface PayFailed {
  type: "PAY_FAILED";
  payload: ErrorPayload;
}
export interface ClearPaymentError {
  type: "CLEAR_PAYMENT_ERROR";
  payload: boolean;
}
export interface CallPaused {
  type: "CALL_PAUSED";
}
export interface CallResumed {
  type: "CALL_RESUMED";
}
export interface CallResumeFailed {
  type: "CALL_RESUME_FAILED";
  payload: string;
}
export interface CallPauseFailed {
  type: "CALL_PAUSE_FAILED";
  payload: string;
}
export interface CallPausing {
  type: "CALL_PAUSING";
}
export interface UpdateCustomerAction {
  type: "UPDATE_CUSTOMER";
  payload: CustomerState;
}
export interface SendReceiptAction {
  type: "SEND_RECEIPT";
}
export interface SendingReceiptAction {
  type: "SENDING_RECEIPT";
}
export interface SendReceiptCompletedAction {
  type: "SEND_RECEIPT_COMPLETED";
  payload: { sendReceiptSuccess: boolean; sendReceiptError: boolean };
}
export interface SendReceiptFailedAction {
  type: "SEND_RECEIPT_FAILED";
  payload: { sendReceiptSuccess: boolean; sendReceiptError: boolean };
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type CallAction =
  | CallResumed
  | CallPaused
  | CallResumeFailed
  | CallPauseFailed
  | CallPausing;
export type KnownAction =
  | NotFoundAction
  | UpdateCustomerAction
  | GetHeaderFooterAction
  | GetHeaderFooterCompletedAction
  | GetHeaderFooterFailedAction
  | GettingHeaderFooterAction
  | FindAction
  | FindCompletedAction
  | FindFailedAction
  | FindingAction
  | StoreAction
  | ClearAction
  | Paying
  | PayCompleted
  | PayFailed
  | ClearPaymentError
  | CallAction
  | SendReceiptAction
  | SendingReceiptAction
  | SendReceiptCompletedAction
  | SendReceiptFailedAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
  getHeaderFooter: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
    //Get Header and Footer for ABG
    if (!config.brand || (config.brand && config.brand) == "abg") {
      console.log("getting ABG header and footer");

      fetch("https://www.alanboswell.com/api/theme/page-elements")
        .catch((reason) => {
          debugger;
          console.log(reason);
          return null;
        })
        .then(async (response) => {
          if (response) {
            try {
              let resp = (await response.json()) as GetHeaderFooterResponse;
              return Promise.resolve(resp);
            } catch (error) {
              console.log(error);
              return Promise.reject(error);
            }
          }
        })
        .then((data) => {
          if (data) {
            dispatch({ type: "GET_HEADER_FOOTER_COMPLETED", payload: data });
          } else {
            dispatch({
              type: "GET_HEADER_FOOTER_FAILED",
              payload: { headerFooterError: true },
            });
          }
        })
        .catch((reason) => {
          dispatch({
            type: "GET_HEADER_FOOTER_FAILED",
            payload: { headerFooterError: true },
          });
        });
    }

    //Get Header and Footer for S-TECH
    if (config.brand && config.brand == "stech") {
      console.log("getting S-TECH header and footer");
      fetch("https://www.s-tech.co.uk/api/theme/page-elements")
        .catch((reason) => {
          debugger;
          console.log(reason);
          return null;
        })
        .then(async (response) => {
          try {
            let resp = (await response.json()) as GetHeaderFooterResponse;
            return Promise.resolve(resp);
          } catch (error) {
            console.log(error);
            return Promise.reject(error);
          }
        })
        .then((data) => {
          if (data) {
            dispatch({ type: "GET_HEADER_FOOTER_COMPLETED", payload: data });
          } else {
            dispatch({
              type: "GET_HEADER_FOOTER_FAILED",
              payload: { headerFooterError: true },
            });
          }
        })
        .catch((reason) =>
          dispatch({
            type: "GET_HEADER_FOOTER_FAILED",
            payload: { headerFooterError: true },
          }),
        );
    }
  },

  find:
    (branch: number, reference: string): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({ type: "CLEAR" });
      dispatch({ type: "FINDING" });

      fetch(
        base + "/api/findcustomer",
        postHeaders(JSON.stringify({ branch, reference })),
      )
        .catch((reason) => {
          console.log(reason);
          return null;
        })
        .then(async (response) => {
          console.log(response);
          if (response.status == 404) {
            return Promise.resolve<FindResponse>({
              name: null,
              firstName: null,
              lastName: null,
              clientRef: null,
              branch: null,
              address1: null,
              address2: null,
              address3: null,
              address4: null,
              postCode: null,
              policies: null,
              haveCustomer: false,
            });
          }
          if (response.status == 500) {
            return Promise.resolve<FindResponse>({
              name: null,
              firstName: null,
              lastName: null,
              clientRef: null,
              branch: null,
              address1: null,
              address2: null,
              address3: null,
              address4: null,
              postCode: null,
              policies: null,
              haveCustomer: null,
            });
          }
          let resp = (await response.json()) as FindResponse;
          resp.haveCustomer = true;
          console.log(resp);
          return Promise.resolve(resp);
        })
        .then((data) => {
          console.log(data);
          if (data && data.haveCustomer) {
            data.clientRef = reference;
            data.branch = branch;
            dispatch({ type: "FIND_COMPLETED", customer: data });
          } else if (data.haveCustomer === false) {
            dispatch({ type: "NOT_FOUND" });
          } else {
            dispatch({ type: "FIND_FAILED", payload: { error: true } });
          }
        });
    },
  store:
    (items: PolicyPaymentItem[]): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({ type: "STORE", payload: items });
    },
  clearPaymentError:
    (): AppThunkAction<ClearPaymentError> => (dispatch, getState) => {
      let p = false;
      if (getState().customer.paymentState == PaymentState.SUCCESS) {
        p = true;
      }
      dispatch({ type: "CLEAR_PAYMENT_ERROR", payload: p });
    },
  pause: (): AppThunkAction<CallAction> => (dispatch, getState) => {
    dispatch({ type: "CALL_PAUSING" });
    fetch(base + "/api/call/pause", putHeadersNoBody())
      .then((data) => data.json() as Promise<CallOperationResponse>)
      //.catch(reason => false)
      .then((ok) => {
        if (ok && ok.success) {
          dispatch({ type: "CALL_PAUSED" });
        } else {
          dispatch({ type: "CALL_PAUSE_FAILED", payload: ok.reason });
        }
      });
  },
  resume: (): AppThunkAction<CallAction> => (dispatch, getState) => {
    fetch(base + "/api/call/resume", putHeadersNoBody())
      .then((data) => data.json() as Promise<CallOperationResponse>)
      .then((data) => {
        if (data.success) {
          dispatch({ type: "CALL_RESUMED" });
        } else {
          dispatch({ type: "CALL_RESUME_FAILED", payload: data.reason });
        }
      });
  },
  pay:
    (
      items: PolicyPaymentItem[],
      total: number,
      directDebit: boolean = false,
      aprInfo: any = null,
    ): AppThunkAction<PayCompleted | PayFailed | ClearPaymentError | Paying> =>
    (dispatch, getState) => {
      console.log("Taking payment...");

      const {
        clientRef,
        policyRef,
        branch,
        address1,
        address2,
        address3,
        address4,
        postCode,
        paymentState,
        firstName,
        lastName,
        name,
        email,
      } = getState().customer;

      console.log(paymentState);

      if (paymentState == PaymentState.PAYING) {
        console.log("Payment already in progress(?) - aborting");
        return;
      }

      dispatch({ type: "CLEAR_PAYMENT_ERROR", payload: false });

      dispatch({ type: "PAYING" });

      //setTimeout(()=>dispatch({ type: 'PAY_FAILED' }), 3000);

      fetch(
        `${base}/api/payment`,
        postHeaders(
          JSON.stringify({
            name,
            total,
            clientRef,
            policyRef,
            branch,
            clientName: name,
            email: email,
            brand: config.brand,
            address: { address1, address2, address3, address4, postCode },
            directDebit,
            aprInfo,
            items: items.map((i) => ({
              policyRef: i.reference,
              name: i.reference
                ? `${i.reference || i.adhocSuffix}-${i.suffix}`
                : "n/a",
              suffix: i.suffix,
              amount: i.amount,
              transactionType: i.transactionType,
              effectiveDate: i.effectiveDate,
              adhoc: i.adhoc || false,
            })),
          }),
        ),
      )
        .then((response) =>
          response.ok
            ? (response.json() as Promise<PaymentResponse>)
            : response.status != 500
              ? (response.json() as Promise<PaymentResponse>)
              : null,
        )
        .then((data) => {
          if (data && data.url && data.token) {
            dispatch({
              type: "PAY_COMPLETED",
              payload: {
                transactionCode: data.transactionCode || null,
                url: data.url,
                token: data.token,
                message: data.message || null,
                redirect: data.redirect || null,
              },
            });
          } else {
            if (data && data.title && data.errors) {
              dispatch({
                type: "PAY_FAILED",
                payload: {
                  message: data.message || null,
                  errorText: data.title,
                  errorItems: [
                    ...Object.keys(data.errors).map((key) => {
                      let k = key.split(".")[1];
                      return { key: k, reasons: data.errors[key] };
                    }),
                  ],
                },
              });
            } else {
              dispatch({
                type: "PAY_FAILED",
                payload: {
                  message: (data && data.message) || null,
                  errorText: null,
                  errorItems: [],
                },
              });
            }
          }
        })
        .catch((reason) => {
          console.log(reason);
          dispatch({
            type: "PAY_FAILED",
            payload: { message: "", errorText: reason.message, errorItems: [] },
          });
        });
    },

  reset: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
    dispatch({ type: "CLEAR" });
  },

  updateCustomerDetails:
    (customer: CustomerState): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Client ref = ${customer.clientRef}`);
      dispatch({ type: "UPDATE_CUSTOMER", payload: customer });
    },

  sendReceipt:
    (email: string, reference: number): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      const { clientRef, branch, name } = getState().customer;

      fetch(
        base + "/api/receipt",
        postHeaders(
          JSON.stringify({
            clientRef,
            branch,
            name,
            ClientName: name,
            reference,
            email,
            Brand: config.brand,
          }),
        ),
      )
        .catch((reason) => {
          debugger;
          console.log(reason);
          return null;
        })
        .then(async (response) => {
          if (response && response.status == 200) {
            dispatch({
              type: "SEND_RECEIPT_COMPLETED",
              payload: { sendReceiptSuccess: true, sendReceiptError: false },
            });
          } else {
            dispatch({
              type: "SEND_RECEIPT_FAILED",
              payload: { sendReceiptSuccess: false, sendReceiptError: true },
            });
          }
        });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<CustomerState> = (
  state: CustomerState | undefined,
  incomingAction: Action,
): CustomerState => {
  if (state === undefined) {
    return defaultState;
  }

  const action = incomingAction as KnownAction;
  //console.log(action);
  switch (action.type) {
    case "GET_HEADER_FOOTER_COMPLETED":
      console.log("get header footer completed");
      return { ...state, ...action.payload, headerFooterError: false };
    case "GET_HEADER_FOOTER_FAILED":
      console.log("get header footer failed");
      return { ...state, headerFooterError: true };
    case "FINDING":
      return { ...state, error: false, haveCustomer: false, error404: false };
    case "FIND_COMPLETED":
      console.log("find completed");
      return {
        ...state,
        ...action.customer,
        error: false,
        haveCustomer: true,
        error404: false,
        updated: new Date(),
      };
    case "FIND_FAILED":
      console.log("find failed");
      return {
        ...state,
        error: true,
        haveCustomer: false,
        updated: new Date(),
        error404: !action.payload.error,
      };
    case "NOT_FOUND":
      console.log("not found");
      return {
        ...state,
        error: false,
        haveCustomer: false,
        updated: new Date(),
        error404: true,
      };
    case "STORE":
      return { ...state, policies: action.payload };
    case "CLEAR":
      return {
        ...state,
        callMessage: null,
        message: null,
        name: null,
        address1: null,
        address2: null,
        address3: null,
        address4: null,
        postCode: null,
        firstName: null,
        lastName: null,
        policies: [],
        haveCustomer: false,
        error: false,
        error404: null,
        paymentState: PaymentState.INITIAL,
        callPaused: CallState.INITIAL,
        paymentTimestamp: null,
      };
    case "PAYING":
      console.log("REDUCER: PAYING");
      return {
        ...state,
        paymentState: PaymentState.PAYING,
        error: false,
        paymentFailure: false,
        paymentSuccess: false,
        paymentTimestamp: null,
        transactionCode: null,
      };
    case "PAY_COMPLETED":
      return {
        ...state,
        message: action.payload.message,
        url: action.payload.url,
        token: action.payload.token,
        redirect: action.payload.redirect,
        paymentState: PaymentState.SUCCESS,
        error: false,
        paymentFailure: false,
        paymentSuccess: true,
        transactionCode: action.payload.transactionCode,
        paymentTimestamp: new Date(),
      };
    case "PAY_FAILED":
      console.log("Changing state to paymentFailure: true");
      return {
        ...state,
        message: action.payload.message,
        validationErrors: action.payload.errorItems,
        paymentState: PaymentState.FAILED,
        error: false,
        paymentFailure: true,
        paymentSuccess: false,
        paymentTimestamp: new Date(),
        transactionCode: null,
      };
    case "CLEAR_PAYMENT_ERROR":
      if (action.payload) {
        return defaultState;
      } else {
        return {
          ...state,
          paymentFailure: null,
          paymentSuccess: null,
          paymentTimestamp: null,
          error: false,
          transactionCode: null,
          message: null,
          validationErrors: [],
        };
      }
    case "CALL_PAUSING":
      return { ...state, callPaused: CallState.PAUSING, callMessage: null };
    case "CALL_PAUSED":
      return { ...state, callPaused: CallState.PAUSED, callMessage: null };
    case "CALL_PAUSE_FAILED":
      return {
        ...state,
        callPaused: CallState.NOT_PAUSED,
        callMessage: action.payload,
      };
    case "CALL_RESUME_FAILED":
      return {
        ...state,
        callPaused: CallState.NOT_RESUMED,
        callMessage: action.payload,
      };
    case "UPDATE_CUSTOMER":
      return { ...state, ...action.payload, updated: new Date() };
    case "SEND_RECEIPT_COMPLETED":
      console.log("send receipt completed");
      return {
        ...state,
        ...action.payload,
        sendReceiptSuccess: true,
        sendReceiptError: false,
      };
    case "SEND_RECEIPT_FAILED":
      console.log("send receipt failed");
      return { ...state, sendReceiptSuccess: false, sendReceiptError: true };
    default:
      return state;
  }
};
